import { useState } from 'react';
import { toast } from 'react-toastify';
import { sendFeedbackMessage } from '../../../functions/API';
import { useScene } from '../../../hooks';
import { useEditor } from '../../../hooks/useEditor';
import { IconButton } from '../../generic/IconButton';
import { TextButton } from '../../generic/TextButton';
import { Dialog } from '../../generic/dialog/Dialog';
import { Icons, IconsSeparator, Item } from '../../layout/sidebar/Icons';
import { Feedback } from '../Feedback';
import { Pages } from '../ToolbarContent';

export const ToolbarButtons = ({
  currentPage,
  setPage,
}: {
  currentPage: Pages;
  setPage: (page: Pages) => void;
}) => {
  const scene = useScene();
  const { UI, setUI, userSession } = useEditor();
  const { theme } = UI;
  const [isFeedbackDialogOpen, setIsFeedbackDialogOpen] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');

  const toggleTheme = () =>
    setUI((UI) => ({ ...UI, theme: UI.theme === 'dark' ? 'light' : 'dark' }));

  return (
    <Icons>
      <div>
        <Item id="element-tree-icon">
          <IconButton
            isActive={currentPage === Pages.Elements}
            isTriangleRight={true}
            handleClick={() => setPage(Pages.Elements)}
          >
            <i className="icon-layers" />
          </IconButton>
        </Item>
        <Item id="assets-icon">
          <IconButton
            isActive={currentPage === Pages.Assets}
            isTriangleRight={true}
            handleClick={() => setPage(Pages.Assets)}
          >
            <i className="icon-assets" />
          </IconButton>
        </Item>
        <Item id="window-icon">
          <IconButton
            isActive={currentPage === Pages.Window}
            isTriangleRight={true}
            handleClick={() => setPage(Pages.Window)}
          >
            <i className="icon-window" />
          </IconButton>
        </Item>

        <IconsSeparator />

        <Item id="add-box-icon">
          <IconButton handleClick={() => scene.addLauncherElement('div')}>
            <i className="icon-box" />
          </IconButton>
        </Item>
        <Item id="add-button-icon">
          <IconButton handleClick={() => scene.addLauncherElement('button')}>
            <i className="icon-interaction" />
          </IconButton>
        </Item>
        <Item id="add-text-icon">
          <IconButton handleClick={() => scene.addLauncherElement('text')}>
            <i className="icon-text" />
          </IconButton>
        </Item>
        <Item id="add-link-icon">
          <IconButton handleClick={() => scene.addLauncherElement('link')}>
            <i className="icon-hyperlink" />
          </IconButton>
        </Item>
        <Item id="add-variable-icon">
          <IconButton
            handleClick={() => scene.addLauncherElement('variableText')}
          >
            <i className="icon-variable" />
          </IconButton>
        </Item>
        <Item id="add-img-icon">
          <IconButton handleClick={() => scene.addLauncherElement('img')}>
            <i className="icon-img" />
          </IconButton>
        </Item>
        <Item id="add-progressbar-icon">
          <IconButton
            handleClick={() => scene.addLauncherElement('appTaskProgressBar')}
          >
            <i className="icon-progressbar" />
          </IconButton>
        </Item>
        <Item id="add-iframe-icon">
          <IconButton handleClick={() => scene.addLauncherElement('iframe')}>
            <i className="icon-iframe" />
          </IconButton>
        </Item>
      </div>

      <div>
        {userSession.isLoggedIn && (
          <Dialog
            title="Send Feedback"
            description="Please describe your issue or share your ideas"
            open={isFeedbackDialogOpen}
            setOpen={setIsFeedbackDialogOpen}
            triggerElement={
              <Item id="feedback-icon">
                <IconButton>
                  <i className="icon-bug" />
                </IconButton>
              </Item>
            }
            cancelElement={<TextButton>Cancel</TextButton>}
            actionElement={
              <TextButton
                handleClick={() => {
                  toast.promise(
                    sendFeedbackMessage({ message: feedbackMessage }),
                    {
                      pending: 'Sending Feedback Message',
                      success: 'Feedback has been sent. Thank you!',
                      error: 'Error while sending the feedback',
                    }
                  );
                }}
                type="accent"
              >
                Send
              </TextButton>
            }
          >
            <Feedback setFeedbackMessage={setFeedbackMessage} />
          </Dialog>
        )}

        <Item id="toggle-theme-icon">
          <IconButton handleClick={() => toggleTheme()}>
            <i className={theme === 'light' ? 'icon-moon' : 'icon-sun'} />
          </IconButton>
        </Item>
      </div>
    </Icons>
  );
};
